import React, { useRef } from 'react';
import { Box, Theme, styled } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import uniqid from 'uniqid';

const Container = styled(Box)(({ theme }: { theme: Theme }) => ({
    whiteSpace: 'nowrap',
    position: 'relative',
    display: 'inline-block',
    border: `1px solid ${theme.palette.grey[400]}`,
    borderRadius: theme.spacing(3),
    backgroundColor: theme.palette.common.white,
    height: 32,
}));

const FlexContainer = styled(Box)({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
});

const Swapper = styled(Box)(({ theme }: { theme: Theme }) => ({
    position: 'absolute',
    borderRadius: theme.spacing(3),
    height: '100%',
    top: 0,
    left: -1,
    backgroundColor: theme.palette.primary.main,
    transition: theme.transitions.create('transform', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.short,
    }),
}));

const Content = styled(Box)(({ theme }: { theme: Theme }) => ({
    position: 'relative',
    display: 'inline-block',
    zIndex: 1,
    flexGrow: 1,
    fontFamily: theme.typography.fontFamily,
    fontSize: '12px',

    '& input': {
        appearance: 'none',
    },

    '& label': {
        position: 'absolute',
        width: '100%',
        top: 0,
        color: theme.status.default.color,
        padding: theme.spacing(0.5, 1),
        textAlign: 'center',
        cursor: 'pointer',
    },

    '& input:checked + label': {
        color: theme.palette.common.white,
    },
}));

// Props Interface
interface PoolGroupButtonProps {
    name: string;
    options?: OptionProps[];
    onChange?: (value: string | undefined) => void;
    value?: string;
    readOnly?: boolean;
}

interface OptionProps {
    value: string;
    prefix: React.ReactNode;
}

export const PoolGroupButton = (props: PoolGroupButtonProps): JSX.Element => {
    const { name, options, value, readOnly, onChange } = props;
    const labelRefs = useRef<HTMLLabelElement[]>([]);

    const buttonIds = options?.map(() => uniqid());
    buttonIds?.push('all');
    const groupId = uniqid();
    const buttonWidth = 100;

    const handleChange = (selectedValue: string | undefined) => () => {
        if (readOnly) return;
        if (onChange) {
            onChange(selectedValue);
        }
    };

    const swapperPositionIndex = options?.findIndex(option => option.value === value) ?? -1;
    const swapperPosition = `${swapperPositionIndex === -1 ? 1 : (swapperPositionIndex + 1) * 100}%`;
    const swapperStyle = {
        width: `${buttonWidth}px`,
        transform: `translateX(${swapperPosition})`,
    };

    return (
        <Container id={groupId}>
            <FlexContainer>
                <Content key={'all'} style={{ width: `${buttonWidth}px` }}>
                    <input
                        type="radio"
                        name={name}
                        id={'all'}
                        checked={value === undefined}
                        onChange={handleChange(undefined)}
                    />
                    <label
                        htmlFor={'all'}
                        ref={el => {
                            if (el) {
                                labelRefs.current[0] = el;
                            }
                        }}
                    >
                        <FormattedMessage id="app.filter.all" />
                    </label>
                </Content>

                {options?.map((option, index) => {
                    const buttonId = buttonIds?.[index];
                    return (
                        <Content key={buttonId} style={{ width: `${buttonWidth}px` }}>
                            <input
                                type="radio"
                                name={name}
                                id={buttonId}
                                checked={value === option.value}
                                onChange={handleChange(option.value)}
                            />
                            <label
                                htmlFor={buttonId}
                                ref={el => {
                                    if (el) {
                                        labelRefs.current[index] = el;
                                    }
                                }}
                            >
                                {option.prefix}
                            </label>
                        </Content>
                    );
                })}
            </FlexContainer>
            <Swapper style={swapperStyle} />
        </Container>
    );
};
